import React, { useCallback, useEffect, useState } from 'react';
import Dashboard from './components/Dashboard';
import Reports from './components/Reports';
import PL from './components/PL';
import Inventory from './components/Inventory';
import Product from './components/Product';
import ProductManagement from './components/ProductManagement';
import FastBilling from './components/FastBilling';
import InvoiceEditor from './components/InvoiceEditor';
import Income from './components/Income';
import Orders from './components/Orders';
import Billing from './components/Billing';
import Accounts from './components/Accounts';
import BusinessAccounts from './components/BusinessAccounts';
import Expenses from './components/Expenses';
import Customers from './components/Customers';
import Categories from './components/Categories';
import AuthContainer from './components/auth/AuthContainer';
import Profile from './components/Profile';
import Admin from './components/Admin';
import AdminPush from './components/AdminPush';
import AdminOrgs from './components/AdminOrgs';
import AdminWhatsApp from './components/AdminWhatsApp';
import AdminQuota from './components/AdminQuota';
import AdminSubscriptionPlans from './components/AdminSubscriptionPlans';
import Tutorial from './components/Tutorial';
import IosInstallModal from './components/IosInstallModal';
import SubscriptionModal from './components/SubscriptionModal';
import PublicInvoice from './components/PublicInvoice';
import ToastHost from './components/system/ToastHost';
import ConfirmHost from './components/system/ConfirmHost';
import { requestFcmPermissionAndToken, onForegroundMessage } from './firebase';
import { userApi } from './api';
import { useI18n } from './i18n';
import SideNav from './components/SideNav';
import { AccountProvider } from './contexts/AccountContext';
import { PermissionProvider, usePermissions } from './contexts/PermissionContext';
import { InvoiceProvider } from './contexts/InvoiceContext';
import AccountIndicator from './components/AccountIndicator';
import UserManagement from './components/UserManagement';
import PrinterSetup from './components/printer/PrinterSetup';
import { filterNavItems, getBestLandingPage } from './utils/permissionChecker';
import PermissionPage from './components/PermissionPage';
import migrationService from './services/migrationService';
import { bootstrapOfflineData } from './services/offline/bootstrapService';
import { cacheSubscriptionStatus, getOfflineStatus, getCachedSubscriptionStatus } from './services/offlineSubscriptionService';
import { syncService } from './services/sync/syncService';
import { dataService } from './services/dataService';
import { printerService } from './services/printer';


const navItems = [
  { key: 'dashboard', icon: '🏠', labelKey: 'nav.dashboard', permission: 'reports:view' }, // Requires reports:view permission
  { key: 'reports', icon: '📊', labelKey: 'nav.reports', permission: 'reports:view' },
  { key: 'pl', icon: '📈', labelKey: 'nav.pl', permission: 'reports:view' },
  { key: 'fast-billing', icon: '⚡', labelKey: 'nav.fast-billing', permission: 'orders:create' }, // Fast billing screen
  { key: 'billing', icon: '📄', labelKey: 'nav.billing', permission: 'orders:view' }, // Billing orders management
  { key: 'invoice-editor', icon: '📝', labelKey: 'nav.invoice-editor', permission: 'orders:create' }, // Invoice editor
  { key: 'income', icon: '💰', labelKey: 'nav.income', permission: 'orders:view' }, // Renamed from orders to income
  { key: 'expenses', icon: '💸', labelKey: 'nav.expenses', permission: 'expenses:view' },
  { key: 'categories', icon: '🏷️', labelKey: 'nav.categories', permission: 'categories:view' }, // Changed from manage to view
  { key: 'customers', icon: '👤', labelKey: 'nav.customers', permission: 'customers:view' }, // Changed from manage to view
  { key: 'product-management', icon: '🛍️', labelKey: 'nav.product-management', permission: 'products:view' }, // Product management
  { key: 'business-accounts', icon: '🏢', labelKey: 'nav.business-accounts', permission: 'business_accounts:view' }, // Business accounts have their own permissions
  { key: 'accounts', icon: '💳', labelKey: 'nav.accounts', permission: 'money_accounts:view' }, // Changed to correct permission
  { key: 'users', icon: '👥', labelKey: 'nav.users', permission: 'users:manage' },
  { key: 'profile', icon: '👤', labelKey: 'nav.profile' }, // No permission required - user's own profile
  { key: 'printer-setup', icon: '🖨️', labelKey: 'nav.printer-setup' },
];

const adminNavItems = [
  { key: 'admin-users', icon: '👥', label: 'Users' },
  { key: 'admin-orgs', icon: '🏢', label: 'Orgs' },
  { key: 'admin-push', icon: '🔔', label: 'Push' },
  { key: 'admin-whatsapp', icon: '💬', label: 'WhatsApp' },
  { key: 'admin-quota', icon: '📊', label: 'Quotas' },
  { key: 'admin-subscription-plans', icon: '💳', label: 'Subscription Plans' },
];

const USER_REFRESH_INTERVAL = 5 * 60 * 1000;

function App() {
  const { t, lang, setLang } = useI18n();
  const [activeSection, setActiveSection] = useState('dashboard');
  const [authed, setAuthed] = useState(!!localStorage.getItem('authToken'));
  const [user, setUser] = useState(null);
  const [subStatus, setSubStatus] = useState({ valid: true });
  const [adminSection, setAdminSection] = useState('admin-users');
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [sideOpen, setSideOpen] = useState(() => {
    try {
      const saved = localStorage.getItem('tp_side_open');
      if (saved !== null) return saved === '1';
    } catch {}
    try {
      if (typeof window !== 'undefined') return window.innerWidth >= 768; // open by default on desktop
    } catch {}
    return true;
  });

  // Check for public invoice route
  const [isPublicInvoice, setIsPublicInvoice] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState(null);
  const [lastUserRefresh, setLastUserRefresh] = useState(0);

  const loadSubscriptionStatus = async ({ force = false, userRef = user } = {}) => {
    if (userRef?.isAdmin) {
      return { valid: true, plan: 'admin' };
    }

    if (!navigator.onLine && !force) {
      const cached = await getCachedSubscriptionStatus();
      if (cached) {
        return { ...cached, offline: true };
      }
      return { valid: true, plan: 'trial', offline: true };
    }

    try {
      const status = await userApi.subscriptionStatus();
      await cacheSubscriptionStatus(status);
      return status;
    } catch (error) {
      const cached = await getCachedSubscriptionStatus();
      if (cached) {
        return { ...cached, offline: true };
      }
      throw error;
    }
  };

  const cacheUserProfile = useCallback(async (profile) => {
    try {
      if (profile) {
        await dataService.setMetadata('cached_user', profile);
      }
    } catch (error) {
      console.error('Failed to cache user profile', error);
    }
  }, []);

  const loadCachedUser = useCallback(async () => {
    try {
      return await dataService.getMetadata('cached_user');
    } catch (error) {
      console.error('Failed to load cached user profile', error);
      return null;
    }
  }, []);

  // Check for public invoice route on mount
  useEffect(() => {
    const checkForPublicInvoice = () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const inv = urlParams.get('inv');
        
        if (inv) {
          setIsPublicInvoice(true);
          setInvoiceNumber(inv); // This will now be the encoded order ID
          return true;
        }
      } catch (error) {
        console.error('Error checking URL parameters:', error);
      }
      return false;
    };

    checkForPublicInvoice();
  }, []);

  useEffect(() => {
    try { localStorage.setItem('tp_side_open', sideOpen ? '1' : '0'); } catch {}
  }, [sideOpen]);

  useEffect(() => {
    // Check auth token once on mount
    const checkAuth = () => {
      const t = !!localStorage.getItem('authToken');
      setAuthed((prev) => {
        if (prev !== t) {
          return t;
        }
        return prev;
      });
    };
    
    // Check immediately
    checkAuth();
    
    // Only check periodically if there's no auth token (to catch login)
    const i = setInterval(() => {
      const hasToken = !!localStorage.getItem('authToken');
      if (!hasToken) {
        const t = !!localStorage.getItem('authToken');
        setAuthed((prev) => {
          if (prev !== t) {
            return t;
          }
          return prev;
        });
      }
    }, 5000);
    
    return () => clearInterval(i);
  }, []);

  useEffect(() => {
    if (!authed) return;
    migrationService.runMigrations().catch((error) => {
      console.error('Migration failed', error);
    });
  }, [authed]);

  useEffect(() => {
    if (!authed) return;
    if (navigator.onLine) return;

    let cancelled = false;
    getCachedSubscriptionStatus()
      .then((cached) => {
        if (!cancelled && cached) {
          setSubStatus({ ...cached, offline: true });
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [authed]);

  useEffect(() => {
    if (authed) {
      syncService.startBackgroundSync();
      return () => syncService.stopBackgroundSync();
    }
    syncService.stopBackgroundSync();
  }, [authed]);

  useEffect(() => {
    if (authed && user) {
      printerService.setUserContext(user);
    } else {
      printerService.setUserContext(null);
    }
  }, [authed, user]);

  // Expose lightweight navigation helper for in-app redirects from components
  useEffect(() => {
    try {
      window.tpNavigate = (section) => {
        if (user?.isAdmin) return; // keep admin pages stable
        const valid = ['dashboard','pl','income','expenses','product','inventory','customers','accounts','profile'];
        if (valid.includes(section)) setActiveSection(section);
      };
    } catch {}
    return () => { try { delete window.tpNavigate; } catch {} };
  }, [user]);

  // GA4 basic events
  useEffect(() => {
    try {
      if (window && window.gtag) {
        window.gtag('event', 'login_state', { authed: authed ? 'yes' : 'no' });
      }
    } catch {}
  }, [authed]);


  useEffect(() => {
    if (!authed) return;
    (async () => {
      try {
        const me = await userApi.me();
        setUser(me);
        await cacheUserProfile(me);
        
        // Trigger permission reload by dispatching a custom event with user data
        if (me) {
          window.dispatchEvent(new CustomEvent('tp_user_updated', { 
            detail: { userData: me } 
          }));
        }
        
        try {
          const status = await loadSubscriptionStatus({ userRef: me });
          setSubStatus(status);
        } catch (err) {
          console.error('Failed to load subscription status', err);
        }
          setLastUserRefresh(Date.now());
        
      } catch (e) {
        // ignore
      }
    })();
  }, [authed]);

  useEffect(() => {
    if (!authed || !user) return;

    let cancelled = false;

    const hydrateOffline = async () => {
      try {
        await bootstrapOfflineData({ user, force: false });
        await syncService.processQueue();
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to bootstrap offline data', error);
        }
      }
    };

    hydrateOffline();

    return () => {
      cancelled = true;
    };
  }, [authed, user]);

  // Handle production scenario: ensure user data is available on app load
  useEffect(() => {
    if (authed && !user) {
      // If user is authenticated but no user data in state, fetch it
      (async () => {
        try {
          const me = await userApi.me();
          setUser(me);
          
          // Trigger permission reload by dispatching a custom event
          if (me) {
            window.dispatchEvent(new CustomEvent('tp_user_updated', { 
              detail: { userData: me } 
            }));
          }
          await cacheUserProfile(me);
        } catch (e) {
          const cached = await loadCachedUser();
          if (cached) {
            setUser(cached);
          } else if (navigator.onLine) {
            localStorage.removeItem('authToken');
            setAuthed(false);
          }
        }
      })();
    }
  }, [authed, user]);

  // Permission-based navigation after login
  const PermissionBasedNavigator = () => {
    const { loading: permissionsLoading, hasPermission } = usePermissions();
    
    useEffect(() => {
      if (!authed || !user || permissionsLoading) return;
      
      // Only run once when permissions are loaded
      if (!permissionsLoaded) {
        const timer = setTimeout(() => {
          setPermissionsLoaded(true);
          
          // Use the generic permission checker to determine best landing page
          const targetSection = getBestLandingPage(hasPermission);
          
          // Only navigate if we're currently on dashboard (initial load)
          if (activeSection === 'dashboard' && targetSection !== 'dashboard') {
            setActiveSection(targetSection);
          }
        }, 1000); // Wait 1 second for permissions to load
        
        return () => clearTimeout(timer);
      }
    }, [authed, user, permissionsLoading, permissionsLoaded, activeSection, hasPermission]);

    // Add a fallback timeout to prevent infinite loading
    useEffect(() => {
      const fallbackTimer = setTimeout(() => {
        if (!permissionsLoaded) {
          setPermissionsLoaded(true);
        }
      }, 5000); // 5 second fallback

      return () => clearTimeout(fallbackTimer);
    }, [permissionsLoaded]);
    
    return null; // This is just a logic component
  };

  // SideNav with permission filtering
  const PermissionFilteredSideNav = () => {
    return (
      <SideNav
        open={sideOpen}
        onClose={() => setSideOpen(false)}
        items={navItems.map(n => ({ ...n, label: t(n.labelKey) }))}
        adminItems={adminNavItems}
        activeKey={user?.isAdmin ? adminSection : activeSection}
        onSelect={(key) => { if (user?.isAdmin) setAdminSection(key); else setActiveSection(key); }}
        user={user}
        isAdmin={!!user?.isAdmin}
      />
    );
  };

  // Refresh user data on screen navigation to get updated subscription status
  useEffect(() => {
    if (!authed || !user) return;
    if (typeof navigator !== 'undefined' && !navigator.onLine) return;

    const now = Date.now();
    if (now - lastUserRefresh < USER_REFRESH_INTERVAL) return;
    setLastUserRefresh(now);

    (async () => {
      try {
        const me = await userApi.me();
        setUser(me);
        await cacheUserProfile(me);
        
        // Trigger permission reload by dispatching a custom event
        if (me) {
          window.dispatchEvent(new CustomEvent('tp_user_updated', { 
            detail: { userData: me } 
          }));
        }
        
        if (!me?.isAdmin) {
          try {
            const status = await loadSubscriptionStatus({ userRef: me, force: true });
            setSubStatus(status);
          } catch (err) {
            console.error('Failed to refresh subscription status', err);
          }
        }
      } catch (e) {
        console.error('Failed to refresh user data on navigation', e);
      }
    })();
  }, [activeSection, authed, user, lastUserRefresh]);

  // Scroll to top when navigating between screens
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeSection, adminSection]);

  // Auto show tutorial for first-time users (client-side only)
  useEffect(() => {
    if (!authed) return;
    try {
      const done = localStorage.getItem('tp_onboarding_done');
      if (!done) setShowTutorial(true);
    } catch {}
  }, [authed]);

  // Always capture beforeinstallprompt early (even before login)
  useEffect(() => {
    let deferredPrompt = null;
    const onBeforeInstall = (e) => {
      console.log('beforeinstallprompt event captured');
      // Prevent the default install prompt from showing immediately
      e.preventDefault();
      // Store the event for later use
      deferredPrompt = e;
      setInstallPrompt(deferredPrompt);
      console.log('Install prompt stored for later use');
    };
    const onAppInstalled = () => setInstallPrompt(null);
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  // Request notifications when logged in
  useEffect(() => {
    if (!authed) return;
    const vapid = process.env.REACT_APP_FCM_VAPID_KEY;
    if (vapid && 'Notification' in window) {
      requestFcmPermissionAndToken(vapid).then(async (t) => {
        try { if (t) await userApi.saveFcmToken(t); } catch {}
      });
      onForegroundMessage((payload) => {
        try {
          const title = payload?.notification?.title || 'TrackProfit';
          const body = payload?.notification?.body || '';
          // Lightweight in-app toast alternative
          console.log('FCM:', title, body);
        } catch {}
      });
    }

  }, [authed]);

  // Track connectivity elegantly
  useEffect(() => {
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  useEffect(() => {
    if (!authed || !user || !isOnline) return;
    bootstrapOfflineData({ user, force: false }).catch((error) => {
      console.error('Failed to refresh offline cache after reconnect', error);
    });
    syncService.processQueue().catch((error) => {
      console.error('Failed to process sync queue after reconnect', error);
    });
  }, [authed, user, isOnline]);

  const retryConnection = async () => {
    try {
      // lightweight ping; if it throws, stay offline
      await fetch('/', { cache: 'no-store' });
      if (navigator.onLine) setIsOnline(true);
    } catch {
      setIsOnline(false);
    }
  };

  const triggerInstall = async () => {
    try {
      const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
      if (isIos && !installPrompt) {
        const evt = new CustomEvent('TP_SHOW_IOS_INSTALL');
        window.dispatchEvent(evt);
        return;
      }
      if (!installPrompt) {
        console.log('No install prompt available');
        return;
      }
      console.log('Triggering install prompt...');
      await installPrompt.prompt();
      const choiceResult = await installPrompt.userChoice;
      console.log('Install prompt choice:', choiceResult);
      setInstallPrompt(null);
    } catch (error) {
      console.error('Error triggering install prompt:', error);
    }
  };


  // Handle public invoice route (no authentication required)
  if (isPublicInvoice) {
    return <PublicInvoice encodedOrderId={invoiceNumber} />;
  }

  if (!authed) {
    return <AuthContainer onAuth={() => setAuthed(true)} />;
  }

  // Show loading if user is not loaded yet
  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  const renderSection = () => {
    if (user?.isAdmin) {
      switch (adminSection) {
        case 'admin-users':
          try { if (window && window.gtag) window.gtag('event', 'screen_view', { screen_name: 'admin-users' }); } catch {}
          return <Admin />;
        case 'admin-orgs':
          try { if (window && window.gtag) window.gtag('event', 'screen_view', { screen_name: 'admin-orgs' }); } catch {}
          return <AdminOrgs />;
        case 'admin-push':
          try { if (window && window.gtag) window.gtag('event', 'screen_view', { screen_name: 'admin-push' }); } catch {}
          return <AdminPush />;
        case 'admin-whatsapp':
          try { if (window && window.gtag) window.gtag('event', 'screen_view', { screen_name: 'admin-whatsapp' }); } catch {}
          return <AdminWhatsApp />;
        case 'admin-quota':
          try { if (window && window.gtag) window.gtag('event', 'screen_view', { screen_name: 'admin-quota' }); } catch {}
          return <AdminQuota />;
        case 'admin-subscription-plans':
          try { if (window && window.gtag) window.gtag('event', 'screen_view', { screen_name: 'admin-subscription-plans' }); } catch {}
          return <AdminSubscriptionPlans />;
        default:
          try { if (window && window.gtag) window.gtag('event', 'screen_view', { screen_name: 'admin-users' }); } catch {}
          return <Admin />;
      }
    }
    switch (activeSection) {
      case 'dashboard':
        try { if (window && window.gtag) window.gtag('event', 'screen_view', { screen_name: 'dashboard' }); } catch {}
        return (
          <PermissionPage pageKey="dashboard">
            <Dashboard user={user} subStatus={subStatus} onSubscribe={() => setShowSubscriptionModal(true)} onOpenProfile={() => setActiveSection('profile')} onGoToOrders={() => setActiveSection('income')} />
          </PermissionPage>
        );
      case 'reports':
        try { if (window && window.gtag) window.gtag('event', 'screen_view', { screen_name: 'reports' }); } catch {}
        return (
          <PermissionPage pageKey="reports">
            <Reports user={user} subStatus={subStatus} onSubscribe={() => setShowSubscriptionModal(true)} />
          </PermissionPage>
        );
      case 'inventory':
        try { if (window && window.gtag) window.gtag('event', 'screen_view', { screen_name: 'inventory' }); } catch {}
        return (
          <PermissionPage pageKey="products">
            <Inventory user={user} subStatus={subStatus} onSubscribe={() => setShowSubscriptionModal(true)} />
          </PermissionPage>
        );
      case 'fast-billing':
        try { if (window && window.gtag) window.gtag('event', 'screen_view', { screen_name: 'fast-billing' }); } catch {}
        return (
          <PermissionPage pageKey="billing">
            <FastBilling user={user} subStatus={subStatus} onSubscribe={() => setShowSubscriptionModal(true)} />
          </PermissionPage>
        );
      case 'billing':
        try { if (window && window.gtag) window.gtag('event', 'screen_view', { screen_name: 'billing' }); } catch {}
        return (
          <PermissionPage pageKey="orders">
            <Billing user={user} subStatus={subStatus} onSubscribe={() => setShowSubscriptionModal(true)} />
          </PermissionPage>
        );
      case 'invoice-editor':
        try { if (window && window.gtag) window.gtag('event', 'screen_view', { screen_name: 'invoice-editor' }); } catch {}
        return (
          <PermissionPage pageKey="billing">
            <InvoiceEditor />
          </PermissionPage>
        );
      case 'billing':
        try { if (window && window.gtag) window.gtag('event', 'screen_view', { screen_name: 'billing' }); } catch {}
        return (
          <PermissionPage pageKey="orders">
            <Billing user={user} subStatus={subStatus} onSubscribe={() => setShowSubscriptionModal(true)} />
          </PermissionPage>
        );
      case 'income':
        try { if (window && window.gtag) window.gtag('event', 'screen_view', { screen_name: 'income' }); } catch {}
        return (
          <PermissionPage pageKey="orders">
            <Orders user={user} subStatus={subStatus} onSubscribe={() => setShowSubscriptionModal(true)} onRefreshSubscription={async () => { const status = await userApi.subscriptionStatus(); setSubStatus(status); }} />
          </PermissionPage>
        );
      case 'customers':
        try { if (window && window.gtag) window.gtag('event', 'screen_view', { screen_name: 'customers' }); } catch {}
        return (
          <PermissionPage pageKey="customers">
            <Customers user={user} subStatus={subStatus} onSubscribe={() => setShowSubscriptionModal(true)} />
          </PermissionPage>
        );
      case 'categories':
        try { if (window && window.gtag) window.gtag('event', 'screen_view', { screen_name: 'categories' }); } catch {}
        return (
          <PermissionPage pageKey="categories">
            <Categories user={user} subStatus={subStatus} onSubscribe={() => setShowSubscriptionModal(true)} />
          </PermissionPage>
        );
      case 'product-management':
        try { if (window && window.gtag) window.gtag('event', 'screen_view', { screen_name: 'product-management' }); } catch {}
        return (
          <PermissionPage pageKey="products">
            <ProductManagement user={user} subStatus={subStatus} onSubscribe={() => setShowSubscriptionModal(true)} />
          </PermissionPage>
        );
      case 'pl':
        try { if (window && window.gtag) window.gtag('event', 'screen_view', { screen_name: 'pl' }); } catch {}
        return (
          <PermissionPage pageKey="pl">
            <PL user={user} subscriptionStatus={subStatus} onSubscribe={() => setShowSubscriptionModal(true)} />
          </PermissionPage>
        );
      case 'expenses':
        try { if (window && window.gtag) window.gtag('event', 'screen_view', { screen_name: 'expenses' }); } catch {}
        return (
          <PermissionPage pageKey="expenses">
            <Expenses user={user} subStatus={subStatus} onSubscribe={() => setShowSubscriptionModal(true)} onRefreshSubscription={async () => { const status = await userApi.subscriptionStatus(); setSubStatus(status); }} />
          </PermissionPage>
        );
      case 'business-accounts':
        try { if (window && window.gtag) window.gtag('event', 'screen_view', { screen_name: 'business-accounts' }); } catch {}
        return (
          <PermissionPage pageKey="business-accounts">
            <BusinessAccounts user={user} subStatus={subStatus} onSubscribe={() => setShowSubscriptionModal(true)} />
          </PermissionPage>
        );
      case 'accounts':
        try { if (window && window.gtag) window.gtag('event', 'screen_view', { screen_name: 'accounts' }); } catch {}
        return (
          <PermissionPage pageKey="accounts">
            <Accounts user={user} subStatus={subStatus} onSubscribe={() => setShowSubscriptionModal(true)} />
          </PermissionPage>
        );
      case 'users':
        try { if (window && window.gtag) window.gtag('event', 'screen_view', { screen_name: 'users' }); } catch {}
        return (
          <PermissionPage pageKey="users">
            <UserManagement user={user} subStatus={subStatus} onSubscribe={() => setShowSubscriptionModal(true)} />
          </PermissionPage>
        );
      case 'profile':
        try { if (window && window.gtag) window.gtag('event', 'screen_view', { screen_name: 'profile' }); } catch {}
        return (
          <PermissionPage pageKey="profile">
            <Profile user={user} subStatus={subStatus} onSubscribed={async ()=>{ const status = await userApi.subscriptionStatus(); setSubStatus(status); }} onSubscribe={() => setShowSubscriptionModal(true)} />
          </PermissionPage>
        );
      case 'printer-setup':
        try { if (window && window.gtag) window.gtag('event', 'screen_view', { screen_name: 'printer-setup' }); } catch {}
        return <PrinterSetup user={user} />;
      case 'admin':
        try { if (window && window.gtag) window.gtag('event', 'screen_view', { screen_name: 'admin-users' }); } catch {}
        return user?.isAdmin ? <Admin /> : <Dashboard user={user} subStatus={subStatus} />;
      default:
        return <Dashboard />;
    }
  };

  return (
    <AccountProvider>
      <PermissionProvider>
        <InvoiceProvider>
          <ToastHost />
          <ConfirmHost />
          <PermissionBasedNavigator />
        <div className="flex min-h-screen w-full overflow-x-hidden bg-white">
        <PermissionFilteredSideNav />
      <div className={`flex-1 flex flex-col min-h-screen min-w-0 transition-[margin] duration-200 ${sideOpen ? 'md:ml-72' : 'md:ml-0'}`}>
      <header className="px-4 sm:px-6 py-3 sm:py-4 border-b border-green-100 bg-white">
        {/* Main header row */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => setSideOpen(s => !s)}
            className="flex items-center gap-2 cursor-pointer"
            aria-label="Go to home"
          >
            <span data-tour="side-nav-toggle" className={`w-9 h-9 border border-green-200 rounded-lg flex items-center justify-center text-green-700 ${sideOpen ? 'bg-green-50' : ''}`}>{sideOpen ? '✕' : '☰'}</span>
            <img src={process.env.PUBLIC_URL + '/logo192.png?v=2'} alt="TrackProfit" className="w-8 h-8 rounded ml-2" />
            <div className="text-green-700 font-bold">TrackProfit</div>
            {user?.isAdmin && <span className="text-[11px] text-gray-500 ml-1">Admin</span>}
          </button>
          <div className="flex items-center gap-3 relative">
            {/* Account Indicator - only show for non-admin users on larger screens */}
            {!user?.isAdmin && (
              <AccountIndicator className="hidden sm:flex" />
            )}
              <ProfileMenu
              t={t}
              lang={lang}
              setLang={setLang}
              onHelp={() => setShowTutorial(true)}
              canInstall={!!installPrompt}
              onInstall={triggerInstall}
              onLogout={async () => {
                localStorage.removeItem('authToken');
                localStorage.removeItem('tp_user');
                localStorage.removeItem('tp_selected_account_id');
                setAuthed(false);

                // Trigger permission reload by dispatching custom event
                window.dispatchEvent(new CustomEvent('tp_user_logout', {
                  detail: { loggedOut: true }
                }));

                try {
                  await dataService.clearAllData();
                } catch (error) {
                  console.error('Failed to clear local data on logout', error);
                }
              }}
              name={(user?.name || user?.userName || 'U')}
              onProfile={() => setActiveSection('profile')}
            />
          </div>
        </div>
        
        {/* Mobile Account Info Row - only show for non-admin users on small screens */}
        {!user?.isAdmin && (
          <div className="mt-2 sm:hidden">
            <AccountIndicator variant="compact" className="w-full justify-center" />
          </div>
        )}
      </header>
      {/* Offline banner */}
      {!isOnline && (
        <div className="px-4 py-3 bg-yellow-50 border-b border-yellow-200 text-yellow-800 text-sm flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span>⚠️</span>
            <div>
              <div className="font-semibold">{t('banner.offline.title')}</div>
              <div className="text-xs">{t('banner.offline.subtitle')}</div>
            </div>
          </div>
          <button onClick={retryConnection} className="text-xs bg-green-600 text-white px-2 py-1 rounded">{t('banner.offline.retry')}</button>
        </div>
      )}
      <div
        className="flex-1 pb-16 md:pb-0"
        style={{ paddingBottom: 'calc(var(--tp-safe-area-bottom, 0px) + 4rem)' }}
      > {/* side nav present; bottom nav on mobile */}
        {renderSection()}
      </div>
      <IosInstallModal />
      {showTutorial && (
        <Tutorial onClose={() => setShowTutorial(false)} onNavigate={(section) => {
          if (user?.isAdmin) return; // keep admin pages stable
          const valid = ['dashboard','pl','income','expenses','product','inventory','customers','accounts'];
          if (valid.includes(section)) setActiveSection(section);
        }} />
      )}
      <SubscriptionModal 
        isOpen={showSubscriptionModal} 
        onClose={() => setShowSubscriptionModal(false)} 
        user={user}
        onSuccess={async () => {
          const status = await userApi.subscriptionStatus();
          setSubStatus(status);
          setShowSubscriptionModal(false);
        }}
      />
      {/* Mobile bottom nav with limited items */}
      {!user?.isAdmin && (
        <nav
          className="fixed bottom-0 left-0 right-0 z-10 bg-white border-t border-green-100 flex justify-around items-center shadow-md md:hidden"
          style={{
            paddingTop: '0.5rem',
            paddingBottom: 'calc(0.5rem + var(--tp-safe-area-bottom, 0px))',
            minHeight: 'calc(4rem + var(--tp-safe-area-bottom, 0px))'
          }}
        >
          <style>
            {`@media (max-width: 360px) { .tp-nav-label { display: none; } }`}
          </style>
          {[{ key: 'dashboard', icon: '🏠', labelKey: 'nav.dashboard' }, { key: 'income', icon: '📑', labelKey: 'nav.income' }, { key: 'expenses', icon: '💸', labelKey: 'nav.expenses' }, { key: 'reports', icon: '📊', labelKey: 'nav.reports' }].map((item) => (
            <button
              key={item.key}
              onClick={() => setActiveSection(item.key)}
              className={`flex flex-col items-center justify-center text-xl focus:outline-none transition-colors duration-150 flex-1 ${
                activeSection === item.key ? 'text-green-600' : 'text-green-300 hover:text-green-500'
              }`}
            >
              <span>{item.icon}</span>
              <span
                className="tp-nav-label block text-[10px] leading-tight mt-1 font-medium max-w-[80px] px-1 truncate"
                title={t(item.labelKey)}
                aria-label={t(item.labelKey)}
              >
                {t(item.labelKey)}
              </span>
            </button>
          ))}
        </nav>
      )}
      </div>
      </div>
        </InvoiceProvider>
      </PermissionProvider>
    </AccountProvider>
  );
}

export default App;

function ProfileMenu({ name, onProfile, onLogout, t, lang, setLang, onHelp, canInstall, onInstall }) {
  const [open, setOpen] = React.useState(false);
  const initials = String(name || 'U').trim().charAt(0).toUpperCase();
  return (
    <div className="relative">
      <button
        aria-label="Profile"
        onClick={() => setOpen((s) => !s)}
        className="w-8 h-8 rounded-full bg-green-100 border border-green-200 text-green-700 font-semibold flex items-center justify-center shadow-sm"
        title="Profile"
      >
        {initials}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-48 bg-white border border-green-100 rounded-lg shadow-md z-20">
          <button className="block w-full text-left text-sm px-3 py-2 hover:bg-green-50" onClick={() => { setOpen(false); onProfile?.(); }}>{t ? t('profile.profile') : 'Profile'}</button>
          <div className="px-3 py-2 border-t border-green-100">
            <div className="text-[11px] text-gray-500 mb-1">Language</div>
            <select
              aria-label="Language"
              value={lang}
              onChange={(e) => { setLang?.(e.target.value); }}
              className="w-full text-xs bg-white border border-green-200 text-green-700 px-2 py-1 rounded"
            >
              <option value="en">English</option>
              <option value="ta">தமிழ்</option>
            </select>
          </div>
          <button className="block w-full text-left text-sm px-3 py-2 hover:bg-green-50" onClick={() => { setOpen(false); onHelp?.(); }}>{t ? t('header.help') : 'Help'}</button>
          {canInstall && (
            <button className="block w-full text-left text-sm px-3 py-2 hover:bg-green-50" onClick={() => { setOpen(false); onInstall?.(); }}>{t ? t('header.installApp') : 'Install App'}</button>
          )}
          <button className="block w-full text-left text-sm px-3 py-2 text-red-600 hover:bg-green-50 border-t border-green-100" onClick={() => { setOpen(false); onLogout?.(); }}>{t ? t('profile.logout') : 'Logout'}</button>
        </div>
      )}
    </div>
  );
}

